import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, Filter, X, Users, User } from "lucide-react";
import { ReportFilters } from "@/services/studentReportingService";

interface ReportFilterPanelProps {
  viewMode: 'all' | 'individual';
  selectedStudentId: string | null;
  filters: ReportFilters;
  students: Array<{ id: string; name: string; email: string }>;
  courses: Array<{ id: string; title: string }>;
  onViewModeChange: (mode: 'all' | 'individual') => void;
  onStudentChange: (studentId: string | null) => void;
  onFiltersChange: (filters: ReportFilters) => void;
  onExport: () => void;
  onClearFilters: () => void;
}

export const ReportFilterPanel = ({
  viewMode,
  selectedStudentId,
  filters,
  students,
  courses,
  onViewModeChange,
  onStudentChange,
  onFiltersChange,
  onExport,
  onClearFilters,
}: ReportFilterPanelProps) => {
  return (
    <div className="space-y-4 p-4 bg-card rounded-lg border">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Report Filters</h3>
        </div>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('all')}
          >
            <Users className="h-4 w-4 mr-2" />
            All Students
          </Button>
          <Button
            variant={viewMode === 'individual' ? 'default' : 'outline'}
            size="sm"
            onClick={() => onViewModeChange('individual')}
          >
            <User className="h-4 w-4 mr-2" />
            Individual
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {viewMode === 'individual' && (
          <div className="space-y-2">
            <Label htmlFor="student-select">Select Student</Label>
            <Select
              value={selectedStudentId || ''}
              onValueChange={(value) => onStudentChange(value || null)}
            >
              <SelectTrigger id="student-select">
                <SelectValue placeholder="Choose a student..." />
              </SelectTrigger>
              <SelectContent>
                {students.map((student) => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.name} ({student.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {viewMode === 'all' && (
          <div className="space-y-2">
            <Label htmlFor="search">Search Students</Label>
            <Input
              id="search"
              placeholder="Name or email..."
              value={filters.searchQuery || ''}
              onChange={(e) =>
                onFiltersChange({ ...filters, searchQuery: e.target.value })
              }
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="status">Status Filter</Label>
          <Select
            value={filters.statuses?.[0] || 'all'}
            onValueChange={(value) =>
              onFiltersChange({
                ...filters,
                statuses: value === 'all' ? [] : [value],
              })
            }
          >
            <SelectTrigger id="status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="date-from">Date From</Label>
          <Input
            id="date-from"
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) =>
              onFiltersChange({ ...filters, dateFrom: e.target.value })
            }
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="date-to">Date To</Label>
          <Input
            id="date-to"
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) =>
              onFiltersChange({ ...filters, dateTo: e.target.value })
            }
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
        <Button size="sm" onClick={onExport}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  );
};
